/**
 * Executive summary view showing a high-level overview of scan findings.
 * Provides a quick snapshot suitable for management review.
 */

import SeverityBadge from '@/components/common/SeverityBadge';
import type { FindingSummary } from '@/types/finding';
import type { Severity } from '@/types/finding';

interface ExecutiveSummaryProps {
  summary: FindingSummary;
  tenantName: string;
}

export default function ExecutiveSummary({ summary, tenantName }: ExecutiveSummaryProps) {
  const severities: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

  return (
    <div className="card space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Executive Summary</h2>
        <p className="text-sm text-gray-500">Security assessment overview for {tenantName}</p>
      </div>

      {/* Total findings */}
      <div className="text-center">
        <p className="text-4xl font-bold text-gray-900">{summary.total}</p>
        <p className="text-sm text-gray-500">Total Findings</p>
      </div>

      {/* Breakdown by severity */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-700">By Severity</h3>
        <div className="space-y-2">
          {severities.map((sev) => {
            const count = summary.by_severity[sev] ?? 0;
            const pct = summary.total > 0 ? (count / summary.total) * 100 : 0;
            return (
              <div key={sev} className="flex items-center gap-3">
                <SeverityBadge severity={sev} />
                <div className="flex-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-gray-400 transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: {
                          critical: '#dc2626', high: '#ea580c', medium: '#ca8a04', low: '#2563eb', info: '#6b7280',
                        }[sev],
                      }}
                    />
                  </div>
                </div>
                <span className="w-10 text-right text-sm font-medium text-gray-700">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status breakdown */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-700">By Status</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {([
            ['open', summary.open],
            ['confirmed', summary.confirmed],
            ['resolved', summary.resolved],
            ['accepted risk', summary.accepted_risk],
            ['false positive', summary.false_positive],
          ] as const).map(([status, count]) => (
            <div key={status} className="rounded-md border p-3 text-center">
              <p className="text-xl font-bold text-gray-900">{count}</p>
              <p className="text-xs capitalize text-gray-500">{status}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
